import type { DateFilterValue } from './constants';

export function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function fmtDateMadrid(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function fmtTimeLocal(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtTimeMadrid(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('ru-RU', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Local start-of-day (offsetDays ago) → UTC ISO string */
export function localDayStart(offsetDays = 0): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offsetDays, 0, 0, 0, 0).toISOString();
}

/** Local end-of-today → UTC ISO string */
export function localDayEnd(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
}

/** Local date string YYYY-MM-DD, offsetDays ago */
export function localDateStr(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toLocaleDateString('en-CA');
}

/**
 * Convert a preset key ("today" | "week" | "month") to ISO start/end for DATE IN (created_at).
 * Returns undefined fields when key is unrecognized.
 */
export function dateInRangeFromPreset(
  key: string | undefined,
): { startDateIn?: string; endDateIn?: string } {
  if (!key) return {};
  if (key === 'today') return { startDateIn: localDayStart(0), endDateIn: localDayEnd() };
  if (key === 'week') return { startDateIn: localDayStart(7), endDateIn: localDayEnd() };
  if (key === 'month') return { startDateIn: localDayStart(30), endDateIn: localDayEnd() };
  return {};
}

/**
 * Convert a preset key ("today" | "week" | "month") to YYYY-MM-DD start/end for DATE OUT (transaction_date).
 */
export function dateOutRangeFromPreset(
  key: string | undefined,
): { startDateOut?: string; endDateOut?: string } {
  if (!key) return {};
  const today = localDateStr(0);
  if (key === 'today') return { startDateOut: today, endDateOut: today };
  if (key === 'week') return { startDateOut: localDateStr(7), endDateOut: today };
  if (key === 'month') return { startDateOut: localDateStr(30), endDateOut: today };
  return {};
}

/** Custom range (YYYY-MM-DD, YYYY-MM-DD) → ISO start/end for DATE IN (created_at). */
export function dateInRangeFromCustom(
  from: string,
  to: string,
): { startDateIn: string; endDateIn: string } {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return {
    startDateIn: new Date(fy, fm - 1, fd, 0, 0, 0, 0).toISOString(),
    endDateIn: new Date(ty, tm - 1, td, 23, 59, 59, 999).toISOString(),
  };
}

/** Custom range (YYYY-MM-DD, YYYY-MM-DD) → plain YYYY-MM-DD for DATE OUT (transaction_date). */
export function dateOutRangeFromCustom(
  from: string,
  to: string,
): { startDateOut: string; endDateOut: string } {
  return { startDateOut: from, endDateOut: to };
}

/** Unified resolver for DATE IN filter → TransactionFilters fragment. */
export function resolveDateInFilter(
  v: DateFilterValue,
): { startDateIn?: string; endDateIn?: string } {
  if (!v) return {};
  if (v.type === 'preset') return dateInRangeFromPreset(v.key);
  if (!v.from || !v.to) return {};
  return dateInRangeFromCustom(v.from, v.to);
}

/** Unified resolver for DATE OUT filter → TransactionFilters fragment. */
export function resolveDateOutFilter(
  v: DateFilterValue,
): { startDateOut?: string; endDateOut?: string } {
  if (!v) return {};
  if (v.type === 'preset') return dateOutRangeFromPreset(v.key);
  if (!v.from || !v.to) return {};
  return dateOutRangeFromCustom(v.from, v.to);
}
